import { storeToRefs } from "pinia"

import { useAppStore } from "@/stores/AppStore"

import gtmAddToCart from "./gtmAddToCart.js"
import gtmCheckout from "./gtmCheckout.js"
import gtmPageView from "./gtmPageView.js"
import gtmPurchase from "./gtmPurchase.js"
import gtmRemoveFromCart from "./gtmRemoveFromCart.js"
import gtmViewItem from "./gtmViewItem.js"
import gtmViewItemList from "./gtmViewItemList.js"
import gtmLogin from "./gtmLogin.js"
import gtmLogout from "./gtmLogout.js"
import { getUserId, setUserIdFromInternalId, clearUserId, initUserIdFromStorage } from "@/services/userIdentity"

initUserIdFromStorage();

type LoggedInCustomerInfo = {
    email: string
    phone: string
}

type PageViewParams = {
    type: string
    url: string
    title?: string
    categories?: [string]
}

type ViewItemListParams = {
    category: any
    products: any
}

type ProductParams = {
    product: any
    isFavorite: boolean
}

type ProductQuantityParams = ProductParams & {
    quantity: number
    source?: string
}

type CheckoutParams = {
    cart: any
    eventName: string
}

export class GtmEventEmitter {
    private static getUserId(): string | null {
        return getUserId();
    }

    static async syncTrackingUserId(): Promise<void> {
        if (getUserId()) return
        const appStore = useAppStore()
        const { getCurrentCustomer, isSignedIn } = storeToRefs(appStore)
        if (isSignedIn.value && getCurrentCustomer.value?.id != null) {
            await setUserIdFromInternalId(getCurrentCustomer.value.id)
        }
    }

    static async login(customer: { id: string }) {
        await setUserIdFromInternalId(customer.id)
        gtmLogin(getUserId() || null)
    }

    static logout() {
        clearUserId()
        gtmLogout()
    }

    static async awaitGtmInitialized(intervalMs: number, timeoutMs: number): Promise<void> {
        function isGtmInitialized(): boolean {
            // assume GTM fully loaded all 3rd-party tags after TikTok tag loads and sets "ttq"
            return !!(window as unknown as { ttq: unknown }).ttq;
        }

        if (isGtmInitialized()) return
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                clearInterval(interval)
                reject("timeout")
            }, timeoutMs)

            const interval = setInterval(() => {
                if (isGtmInitialized()) {
                    clearInterval(interval)
                    clearTimeout(timer)
                    resolve()
                }
            }, intervalMs)
        })
    }

    static async pageView(params: PageViewParams) {
        await this.syncTrackingUserId();
        gtmPageView(this.getLoggedInCustomerInfo(), params)
    }

    static viewItemList(params: ViewItemListParams) {
        gtmViewItemList(
            this.getLoggedInCustomerInfo(),
            params.category,
            params.products,
        )
    }

    static async viewItem(params: ProductParams) {
        // wait up to 5 seconds for GTM to initialize
        try {
            await this.awaitGtmInitialized(100, 5_000);
        } catch {}
        gtmViewItem(
            this.getLoggedInCustomerInfo(),
            params.product,
            { isFavorite: params.isFavorite },
        )
    }

    static addToCart(params: ProductQuantityParams) {
        gtmAddToCart(
            this.getLoggedInCustomerInfo(),
            params.product,
            {
                quantity: params.quantity,
                isFavorite: params.isFavorite,
                source: params.source,
            },
        )
    }

    static removeFromCart(params: ProductQuantityParams) {
        gtmRemoveFromCart(
            this.getLoggedInCustomerInfo(),
            params.product,
            {
                quantity: params.quantity,
                isFavorite: params.isFavorite,
                source: params.source,
            },
        )
    }

    static checkout(params: CheckoutParams) {
        gtmCheckout(
            this.getLoggedInCustomerInfo(),
            params.cart,
            params.eventName,
        )
    }

    static purchase(order: any) {
        gtmPurchase(order)
    }

    static getLoggedInCustomerInfo(): LoggedInCustomerInfo | null {
        const appStore = useAppStore()
        const { getCurrentCustomer, isSignedIn } = storeToRefs(appStore)

        if (!isSignedIn.value) return null

        const customer: any = getCurrentCustomer.value
        return {
            email: customer.email,
            phone: `${customer.phonePrefix}${customer.phone}`,
        }
    }
}
